import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay';
import { youtubeWatchUrl } from 'fym-shared/src/music24/addedAssets';
import type { SongMusicVideoSummary } from 'fym-shared/src/music24/musicVideoApi';
import { TypedMemo } from '../../../../../common/types';
import { getFullAPIFileURL } from '../../../../../common/getFullAPIFileURL';
import { useCreateKitAssetStreamLinkMutation } from '../../../../../../redux/api/addedAssetsApi';
import { formatClock } from '../../../../../music24/promote/addedAssets/addedAssetFacts';
import { playsInline } from './musicVideoFieldState';
import { musicVideoFieldMessages as messages } from './musicVideoFieldMessages';
import styles from './MusicVideoBox.module.scss';

interface MusicVideoBoxProps {
  accountId: number;
  catalogItemUuid: string;
  summary: SongMusicVideoSummary;
  /** The badge at the bottom left ("LaziiBee", "Your file · 4K", "Cut 2"). */
  badge: string | null;
}

/** The poster of the box: the YouTube picture of a link, else the poster file (a relative API path). */
function posterOf(summary: SongMusicVideoSummary): string | null {
  if (summary.youtube !== null) {
    return summary.youtube.thumbnailUrl;
  }
  return summary.posterUrl === null ? null : getFullAPIFileURL(summary.posterUrl);
}

/**
 * The 16:9 box of a music video (design music-video g2): the poster, the length and the badge, and a
 * play button. A file plays in the page from a 1-hour stream link (`POST .../assets/:uuid/stream`,
 * §4.7); a YouTube link opens YouTube.
 */
const MusicVideoBoxComponent: React.FC<MusicVideoBoxProps> = ({ accountId, catalogItemUuid, summary, badge }) => {
  const intl = useIntl();
  const [createLink, linkState] = useCreateKitAssetStreamLinkMutation();
  // The link belongs to one asset: a replaced video (another asset uuid) shows its own poster again.
  const [stream, setStream] = useState<{ assetUuid: string; url: string } | null>(null);
  const streamUrl = stream !== null && stream.assetUuid === summary.assetUuid ? stream.url : null;
  const poster = posterOf(summary);
  const length = formatClock(summary.durationSec);
  const label = intl.formatMessage(messages.play);

  const play = useCallback(() => {
    const { assetUuid } = summary;
    createLink({ accountId, catalogItemUuid, assetUuid })
      .unwrap()
      .then((link) => setStream({ assetUuid, url: link.url }))
      .catch(console.error);
  }, [accountId, catalogItemUuid, createLink, summary]);

  if (streamUrl !== null) {
    return (
      <div className={styles.box}>
        {/* The artist's own video: it has no captions file. */}
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video src={streamUrl} controls autoPlay playsInline data-testid="music-video-player" />
      </div>
    );
  }

  let playButton: React.ReactNode = null;
  if (summary.youtube !== null) {
    playButton = (
      <a
        className={styles.play}
        href={youtubeWatchUrl(summary.youtube.videoId)}
        target="_blank"
        rel="noreferrer"
        aria-label={label}
      >
        <FontAwesomeIcon icon={faPlay} />
      </a>
    );
  } else if (playsInline(summary)) {
    playButton = (
      <button type="button" className={styles.play} onClick={play} disabled={linkState.isLoading} aria-label={label}>
        <FontAwesomeIcon icon={faPlay} />
      </button>
    );
  }

  return (
    <>
      <div className={styles.box} data-testid="music-video-box">
        {poster !== null && <img src={poster} alt="" />}
        {playButton}
        {badge !== null && <span className={styles.badge}>{badge}</span>}
        {length !== null && <span className={styles.dur}>{length}</span>}
      </div>
      {linkState.isError && (
        <span className={styles.warn} role="alert">
          {intl.formatMessage(messages.playFailed)}
        </span>
      )}
    </>
  );
};

export const MusicVideoBox = TypedMemo(MusicVideoBoxComponent);
